import React, { Component } from 'react';
import PropTypes from 'prop-types';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrDataTableUI from 'client/components/SrDataTableUI';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrTooltip, { SR_TOOLTIP_POSITION_CENTER_UNDERNEATH } from 'client/components/SrTooltip';

import {
  inputStyle,
  paginationLinkStyle,
  activePaginationLinkStyle,
  disabledPaginationLinkStyle,
  ellipsisStyle,
} from './styles';
import SrButton, {
  SR_BUTTON_HEIGHT_SHORTER,
  SR_BUTTON_THEME_ADOBE_BLUE,
} from '../SrButton/SrButton';

/**
 * Pagination link component
 */
const PaginationLink = ({ children, onClick, active = false, disabled = false }) => (
  <div
    onClick={disabled ? undefined : onClick}
    style={
      disabled
        ? disabledPaginationLinkStyle
        : active
        ? activePaginationLinkStyle
        : paginationLinkStyle
    }
    role={disabled ? 'presentation' : 'button'}
    tabIndex={disabled ? -1 : 0}
    onKeyDown={(e) => {
      if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
        onClick(e);
      }
    }}
  >
    {children}
  </div>
);

PaginationLink.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
};

/**
 * UPProvidersTable component - Table displaying utility providers with filtering and pagination
 */
class UPProvidersTable extends Component {
  static propTypes = {
    providers: PropTypes.array,
    utilityTypes: PropTypes.array,
    onEdit: PropTypes.func.isRequired,
    totalCount: PropTypes.number,
    isLoading: PropTypes.bool,
    onLoadData: PropTypes.func.isRequired,
    currentPage: PropTypes.number.isRequired,
    itemsPerPage: PropTypes.number.isRequired,
    searchInput: PropTypes.string.isRequired,
    selectedUtilityTypeId: PropTypes.string.isRequired,
  };

  static defaultProps = {
    providers: [],
    utilityTypes: [],
    totalCount: 0,
    isLoading: false,
  };

  _onSearchInputChange = (e) => {
    const searchTerm = e?.target?.value || '';
    this.props.onLoadData({
      currentPage: 1,
      itemsPerPage: this.props.itemsPerPage,
      searchInput: searchTerm,
      selectedUtilityTypeId: this.props.selectedUtilityTypeId,
    });
  };

  _onUtilityTypeFilterChange = (e) => {
    const utilityTypeId = e?.target?.value || '';
    this.props.onLoadData({
      currentPage: 1,
      itemsPerPage: this.props.itemsPerPage,
      searchInput: this.props.searchInput,
      selectedUtilityTypeId: utilityTypeId,
    });
  };

  _handlePageChange = (page) => {
    this.props.onLoadData({
      currentPage: page,
      itemsPerPage: this.props.itemsPerPage,
      searchInput: this.props.searchInput,
      selectedUtilityTypeId: this.props.selectedUtilityTypeId,
    });
  };

  _handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    this.props.onLoadData({
      currentPage: 1,
      itemsPerPage: newItemsPerPage,
      searchInput: this.props.searchInput,
      selectedUtilityTypeId: this.props.selectedUtilityTypeId,
    });
  };

  render() {
    const {
      utilityTypes,
      providers = [],
      onEdit,
      totalCount,
      isLoading,
      currentPage,
      itemsPerPage,
      searchInput,
      selectedUtilityTypeId,
    } = this.props;

    const totalPages = Math.max(Math.ceil(totalCount / itemsPerPage), 1);
    const startIndex = (currentPage - 1) * itemsPerPage;

    const dataTableHeaders = [
      { content: 'Name' },
      { content: 'Utility Type' },
      { content: 'eiaId' },
      { content: 'Actions', style: { textAlign: 'center' } },
    ];

    const providerRows = Array.isArray(providers)
      ? providers.map((provider) => ({
          cells: [
            {
              content: provider.title,
            },
            {
              content: (
                <div style={{ textAlign: 'center' }}>
                  <SrTooltip
                    position={SR_TOOLTIP_POSITION_CENTER_UNDERNEATH}
                    icon={
                      <img
                        src={provider.utilityType?.iconUrl || '/images/utility-default-icon.svg'}
                        alt={provider.utilityType?.label || 'Unknown'}
                        style={{ width: '20px', height: '20px' }}
                      />
                    }
                  >
                    {provider.utilityType?.label || 'Unknown'}
                  </SrTooltip>
                </div>
              ),
            },
            { content: provider.energyInformationAdministrationId || '-' },
            {
              content: (
                <SrFlex justify="center" align="center">
                  <SrButton
                    onClick={() => onEdit(provider)}
                    width="95%"
                    heightType={SR_BUTTON_HEIGHT_SHORTER}
                    theme={SR_BUTTON_THEME_ADOBE_BLUE}
                  >
                    Edit
                  </SrButton>
                </SrFlex>
              ),
              style: { padding: '8px 10px' },
            },
          ],
        }))
      : [];

    // Get page numbers to show in pagination
    const getPageNumbers = () => {
      return Array.from({ length: totalPages }, (_, i) => i + 1).filter((page) => {
        return (
          page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)
        );
      });
    };

    const pageNumbers = getPageNumbers();

    return (
      <>
        <SrFlex justify="between" align="center" columnGap="20px" mb="20px">
          <SrBox width="300px" height="40px">
            <input
              value={searchInput}
              type="text"
              onChange={this._onSearchInputChange}
              placeholder="Search by provider name"
              style={inputStyle}
              disabled={isLoading}
            />
          </SrBox>

          <SrBox width="250px">
            <select
              value={selectedUtilityTypeId}
              onChange={this._onUtilityTypeFilterChange}
              style={inputStyle}
              disabled={isLoading}
            >
              <option value="">All utility types</option>
              {utilityTypes?.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </SrBox>
        </SrFlex>

        <SrDataTableUI
          sticky
          theme={SR_TABLE_THEME_PARTNER_CLEAR}
          headers={dataTableHeaders}
          minWidth="100%"
          rows={providerRows}
        />

        {isLoading && (
          <SrBox backgroundColor="white" display="flex" justifyContent="center" padding="20px">
            <SrText font="greycliff" size="16">
              Loading...
            </SrText>
          </SrBox>
        )}

        {!isLoading && providers.length === 0 && (
          <SrBox backgroundColor="white" display="flex" justifyContent="center" padding="20px">
            <SrText font="greycliff" size="16" fontStyle="italic">
              No providers found matching your criteria.
            </SrText>
          </SrBox>
        )}

        {providers.length > 0 && (
          <SrBox backgroundColor="white" padding="15px">
            <SrFlex justify="between" align="center" wrap="wrap">
              {/* Items per page selector */}
              <SrFlex align="center" mb="10px">
                <SrBox mr="10px">
                  <SrText font="greycliff" size="14">
                    Show
                  </SrText>
                </SrBox>
                <select
                  value={itemsPerPage}
                  onChange={this._handleItemsPerPageChange}
                  style={{
                    ...inputStyle,
                    width: '60px',
                    marginRight: '10px',
                    height: '32px',
                  }}
                  disabled={isLoading}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
                <SrText font="greycliff" size="14">
                  per page
                </SrText>
              </SrFlex>

              {/* Pagination */}
              <SrFlex justify="center" align="center" mb="10px">
                <PaginationLink
                  onClick={() => this._handlePageChange(1)}
                  disabled={currentPage === 1 || isLoading}
                >
                  First
                </PaginationLink>

                {currentPage > 1 && (
                  <PaginationLink
                    onClick={() => this._handlePageChange(currentPage - 1)}
                    disabled={isLoading}
                  >
                    Prev
                  </PaginationLink>
                )}

                {pageNumbers.map((page) => {
                  const showEllipsisBefore =
                    pageNumbers.indexOf(page) > 0 &&
                    pageNumbers[pageNumbers.indexOf(page) - 1] !== page - 1;
                  const showEllipsisAfter =
                    pageNumbers.indexOf(page) < pageNumbers.length - 1 &&
                    pageNumbers[pageNumbers.indexOf(page) + 1] !== page + 1;

                  return (
                    <React.Fragment key={`page-${page}`}>
                      {showEllipsisBefore && <div style={ellipsisStyle}>...</div>}

                      <PaginationLink
                        onClick={() => this._handlePageChange(page)}
                        active={currentPage === page}
                        disabled={isLoading}
                      >
                        {page}
                      </PaginationLink>

                      {showEllipsisAfter && <div style={ellipsisStyle}>...</div>}
                    </React.Fragment>
                  );
                })}

                {currentPage < totalPages && (
                  <PaginationLink
                    onClick={() => this._handlePageChange(currentPage + 1)}
                    disabled={isLoading}
                  >
                    Next
                  </PaginationLink>
                )}

                {currentPage < totalPages && (
                  <PaginationLink
                    onClick={() => this._handlePageChange(totalPages)}
                    disabled={isLoading}
                  >
                    Last
                  </PaginationLink>
                )}
              </SrFlex>

              {/* Results count */}
              <SrBox mb="10px" textAlign="right">
                <SrText font="greycliff" size="13">
                  Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, totalCount)} of{' '}
                  {totalCount} providers
                </SrText>
              </SrBox>
            </SrFlex>
          </SrBox>
        )}
      </>
    );
  }
}

export default UPProvidersTable;
